import {XYCoord} from 'react-dnd';

import {
  DropLinePositionsEnum,
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
} from './workspaceDragDropTypes';

/**
 * @param droppedItem The drag source item being dragged and dropped.
 * @param targetLineItem The item at the drop target line.
 */
export function canDropWorkspaceItemAtTarget(
  droppedItem: WorkspaceDragItem,
  targetLineItem: WorkspaceDragItem | undefined,
  allowSameId = false,
): boolean {
  // If we don't even have an item on the line, abort.
  if (!targetLineItem) {
    return false;
  }

  // If we don't have the same type, abort.
  if (droppedItem.type !== targetLineItem.type) {
    return false;
  }

  // By default, don't allow dropping an item onto itself.
  if (droppedItem.id === targetLineItem.id && !allowSameId) {
    return false;
  }

  // If we're dragging a team inbox or a team view, then we need to compare the namespaces.
  if (
    (droppedItem.type === WorkspaceDragSourceTypesEnum.TEAM_VIEW &&
      targetLineItem.type === WorkspaceDragSourceTypesEnum.TEAM_VIEW) ||
    (droppedItem.type === WorkspaceDragSourceTypesEnum.TEAM_INBOX &&
      targetLineItem.type === WorkspaceDragSourceTypesEnum.TEAM_INBOX)
  ) {
    const isSameNamespace = droppedItem.namespace === targetLineItem.namespace;
    if (!allowSameId) {
      return droppedItem.id !== targetLineItem.id && isSameNamespace;
    }

    return isSameNamespace;
  }

  // If we're trying to drag inside/next to a child tag, we must check the namespace because you can't nest a team tag inside a personal tag and vice-versa.
  if (
    droppedItem.type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW &&
    targetLineItem.type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW &&
    (targetLineItem.hasExpandedChildren || targetLineItem.nestingLevel > 0)
  ) {
    const isSameNamespace = droppedItem.namespace === targetLineItem.namespace;
    const areIdsAreAllowed = !allowSameId ? droppedItem.id !== targetLineItem.id : true;
    if (targetLineItem.nestingLevel > 0) {
      return areIdsAreAllowed && isSameNamespace;
    }

    return areIdsAreAllowed;
  }

  // Otherwise, you can drop.
  return true;
}

export function computeNewIndex(
  droppedItemIndex: number,
  targetIndex: number,
  linePosition: DropLinePositionsEnum,
) {
  if (linePosition === DropLinePositionsEnum.NONE) {
    return droppedItemIndex;
  }

  if (linePosition === DropLinePositionsEnum.ABOVE) {
    return targetIndex > droppedItemIndex ? targetIndex - 1 : targetIndex;
  }

  return targetIndex > droppedItemIndex ? targetIndex : targetIndex + 1;
}

interface ComputeWorkspaceDragAndDropLinePositionOptions {
  dragClientOffset: XYCoord | null;
  dropSourceRect: DOMRect;
  canDrop?: boolean;
}

export function computeWorkspaceDragAndDropLinePosition({
  dragClientOffset,
  dropSourceRect,
  canDrop,
}: ComputeWorkspaceDragAndDropLinePositionOptions): DropLinePositionsEnum {
  if (!dragClientOffset) {
    return DropLinePositionsEnum.NONE;
  }

  const {height, top} = dropSourceRect;
  const centerCoord = height / 2 + top;
  const centerTolerance = 6;

  if (canDrop) {
    if (dragClientOffset.y >= centerCoord + centerTolerance) {
      return DropLinePositionsEnum.BELOW;
    }

    if (dragClientOffset.y <= centerCoord - centerTolerance) {
      return DropLinePositionsEnum.ABOVE;
    }

    return DropLinePositionsEnum.NONE;
  }

  if (dragClientOffset.y >= centerCoord) {
    return DropLinePositionsEnum.BELOW;
  }

  return DropLinePositionsEnum.ABOVE;
}
